import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { Mail, Send, AlertTriangle, CheckCircle, Loader2, Info, X } from 'lucide-react';

interface AuthorityReportStats {
  local_authority: string;
  total_reports: number;
  open_reports: number;
  reports_without_comments: number;
  reports_needing_chaser1: number;
  reports_needing_chaser2: number;
  oldest_unresponded_days: number;
}

interface SendResult {
  success: boolean;
  message: string;
  authorities_notified: number;
  total_reports: number;
  results?: Array<{
    authority: string;
    email: string;
    report_count: number;
    success: boolean;
    error?: string;
    email_id?: string;
  }>;
}

export function EmailAutomation() {
  const [stats, setStats] = useState<AuthorityReportStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState<1 | 2 | null>(null);
  const [sendingAuthority, setSendingAuthority] = useState<string | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [daysThreshold, setDaysThreshold] = useState(7);

  useEffect(() => {
    fetchStats();
  }, [daysThreshold]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      let allReports: any[] = [];
      let offset = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data: batch, error: reportsError } = await supabase
          .from('verge_conditions')
          .select('local_authority, status, comment_count, report_sent_at, chaser1_sent_at, chaser2_sent_at, timestamp')
          .not('local_authority', 'is', null)
          .range(offset, offset + batchSize - 1);

        if (reportsError) throw reportsError;

        if (batch && batch.length > 0) {
          allReports = [...allReports, ...batch];
          offset += batch.length;
          hasMore = batch.length === batchSize;
        } else {
          hasMore = false;
        }

        if (offset > 100000) {
          console.warn('Reached safety limit of 100,000 records');
          break;
        }
      }

      const thresholdDate = new Date();
      thresholdDate.setDate(thresholdDate.getDate() - daysThreshold);

      const statsByAuthority: Record<string, AuthorityReportStats> = {};

      allReports.forEach((report) => {
        const authority = report.local_authority;
        if (!statsByAuthority[authority]) {
          statsByAuthority[authority] = {
            local_authority: authority,
            total_reports: 0,
            open_reports: 0,
            reports_without_comments: 0,
            reports_needing_chaser1: 0,
            reports_needing_chaser2: 0,
            oldest_unresponded_days: 0
          };
        }

        const s = statsByAuthority[authority];
        s.total_reports++;

        if (report.status === 'open') {
          s.open_reports++;
        }

        if (report.comment_count === 0 && report.status === 'open') {
          s.reports_without_comments++;

          const reportDate = new Date(report.report_sent_at || report.timestamp);
          const daysOld = Math.ceil((Date.now() - reportDate.getTime()) / (1000 * 60 * 60 * 24));
          s.oldest_unresponded_days = Math.max(s.oldest_unresponded_days, daysOld);

          const effectiveDate = new Date(report.report_sent_at || report.timestamp);
          if (effectiveDate < thresholdDate) {
            if (!report.chaser1_sent_at) {
              s.reports_needing_chaser1++;
            } else if (!report.chaser2_sent_at) {
              s.reports_needing_chaser2++;
            }
          }
        }
      });

      setStats(Object.values(statsByAuthority).sort((a, b) =>
        b.reports_without_comments - a.reports_without_comments
      ));
    } catch (error: any) {
      console.error('Error fetching stats:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const callChaserEndpoint = async (chaserLevel: 1 | 2, localAuthority?: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-chaser-emails`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chaser_level: chaserLevel,
          days_threshold: daysThreshold,
          ...(localAuthority ? { local_authority: localAuthority } : {})
        })
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send chaser emails');
    }

    return data as SendResult;
  };

  const handleSendChasers = async (chaserLevel: 1 | 2) => {
    const count = chaserLevel === 1 ? totalNeedingChaser1 : totalNeedingChaser2;
    const authorityCount = stats.filter(s =>
      chaserLevel === 1 ? s.reports_needing_chaser1 > 0 : s.reports_needing_chaser2 > 0
    ).length;

    if (!confirm(
      `Send Chaser #${chaserLevel} emails?\n\n` +
      `This will email ${authorityCount} authorities about ${count} reports.\n\n` +
      `Are you sure?`
    )) {
      return;
    }

    try {
      setSending(chaserLevel);
      setError(null);
      setResult(null);

      const data = await callChaserEndpoint(chaserLevel);
      setResult(data);
      await fetchStats();
    } catch (error: any) {
      console.error('Error sending chasers:', error);
      setError(error.message);
    } finally {
      setSending(null);
    }
  };

  const handleSendChaserForAuthority = async (chaserLevel: 1 | 2, authorityName: string) => {
    if (!confirm(`Send Chaser #${chaserLevel} email to ${authorityName}?`)) {
      return;
    }

    try {
      setSendingAuthority(`${authorityName}-${chaserLevel}`);
      setError(null);
      setResult(null);

      const data = await callChaserEndpoint(chaserLevel, authorityName);
      setResult(data);
      await fetchStats();
    } catch (error: any) {
      console.error('Error sending chaser:', error);
      setError(error.message);
    } finally {
      setSendingAuthority(null);
    }
  };

  const totalNeedingChaser1 = stats.reduce((sum, s) => sum + s.reports_needing_chaser1, 0);
  const totalNeedingChaser2 = stats.reduce((sum, s) => sum + s.reports_needing_chaser2, 0);
  const totalUnresponded = stats.reduce((sum, s) => sum + s.reports_without_comments, 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Mail className="w-6 h-6" />
          Email Automation
        </h2>
        <p className="text-gray-600 mt-1">
          Manage automated email reminders for unresponded reports
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-4 rounded-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <strong className="text-green-900">
                {result.authorities_notified > 0 ? 'Emails Sent Successfully' : 'No Emails Sent'}
              </strong>
            </div>
            <button onClick={() => setResult(null)} className="text-green-400 hover:text-green-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <div className="bg-white/60 rounded px-3 py-2">
              <div className="text-lg font-bold text-green-700">{result.authorities_notified}</div>
              <div className="text-xs text-green-600">Authorities Emailed</div>
            </div>
            <div className="bg-white/60 rounded px-3 py-2">
              <div className="text-lg font-bold text-green-700">{result.total_reports}</div>
              <div className="text-xs text-green-600">Reports Covered</div>
            </div>
            {result.results && result.results.filter(r => !r.success).length > 0 && (
              <div className="bg-red-50 rounded px-3 py-2">
                <div className="text-lg font-bold text-red-600">
                  {result.results.filter(r => !r.success).length}
                </div>
                <div className="text-xs text-red-500">Failed</div>
              </div>
            )}
          </div>

          {result.results && result.results.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer font-medium text-green-700 hover:text-green-900">
                View details per authority
              </summary>
              <div className="mt-2 space-y-1">
                {result.results.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between px-3 py-1.5 rounded text-xs ${
                      r.success ? 'bg-white/60' : 'bg-red-50'
                    }`}
                  >
                    <span className="font-medium">{r.authority}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-gray-500">{r.email}</span>
                      <span className="text-gray-400">|</span>
                      <span>{r.report_count} reports</span>
                      {r.success ? (
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <span className="text-red-500" title={r.error}>Failed</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Chaser Emails Work:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Chaser #1:</strong> Sent to authorities with reports older than {daysThreshold} days that have no comments</li>
              <li><strong>Chaser #2:</strong> Sent to authorities that received Chaser #1 but still haven't responded</li>
              <li>Only open reports are included in chasers</li>
              <li>Reports with comments are excluded (even if not closed)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Settings</h3>
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Days Threshold (reports older than this will trigger chasers)
          </label>
          <input
            type="number"
            min="1"
            max="90"
            value={daysThreshold}
            onChange={(e) => setDaysThreshold(parseInt(e.target.value))}
            className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-gray-500 mt-1">
            Current threshold: Reports older than {daysThreshold} days
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
          <div className="text-3xl font-bold text-orange-700">{totalUnresponded}</div>
          <div className="text-sm text-gray-600 mt-1">Unresponded Reports</div>
          <div className="text-xs text-gray-500 mt-1">Open reports with no comments</div>
        </div>
        <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
          <div className="text-3xl font-bold text-yellow-700">{totalNeedingChaser1}</div>
          <div className="text-sm text-gray-600 mt-1">Need Chaser #1</div>
          <div className="text-xs text-gray-500 mt-1">Over {daysThreshold} days, no chaser sent</div>
        </div>
        <div className="bg-red-50 p-6 rounded-lg border border-red-200">
          <div className="text-3xl font-bold text-red-700">{totalNeedingChaser2}</div>
          <div className="text-sm text-gray-600 mt-1">Need Chaser #2</div>
          <div className="text-xs text-gray-500 mt-1">Chaser #1 sent, still no response</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-yellow-600" />
            Send Chaser #1 Emails
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            First reminder for reports older than {daysThreshold} days with no response.
            This will send consolidated emails to authorities with unresponded reports.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="text-sm text-yellow-800">
              <strong>{totalNeedingChaser1}</strong> reports from <strong>{stats.filter(s => s.reports_needing_chaser1 > 0).length}</strong> authorities will receive reminders
            </div>
          </div>
          <button
            onClick={() => handleSendChasers(1)}
            disabled={sending !== null || sendingAuthority !== null || totalNeedingChaser1 === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {sending === 1 ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send Chaser #1 ({totalNeedingChaser1} reports)
              </>
            )}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            Send Chaser #2 Emails
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Final urgent reminder for reports that received Chaser #1 but still have no response.
            Warns of potential escalation.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="text-sm text-red-800">
              <strong>{totalNeedingChaser2}</strong> reports from <strong>{stats.filter(s => s.reports_needing_chaser2 > 0).length}</strong> authorities will receive urgent reminders
            </div>
          </div>
          <button
            onClick={() => handleSendChasers(2)}
            disabled={sending !== null || sendingAuthority !== null || totalNeedingChaser2 === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {sending === 2 ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send Chaser #2 ({totalNeedingChaser2} reports)
              </>
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-800">Authority Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Authority
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Open
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No Comments
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Need C#1
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Need C#2
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oldest
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Send Chaser
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.map((stat) => {
                  const isSendingC1 = sendingAuthority === `${stat.local_authority}-1`;
                  const isSendingC2 = sendingAuthority === `${stat.local_authority}-2`;
                  const isDisabled = sendingAuthority !== null || sending !== null;

                  return (
                    <tr key={stat.local_authority} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                        {stat.local_authority}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center text-gray-500">
                        {stat.total_reports}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center text-gray-500">
                        {stat.open_reports}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        <span className={stat.reports_without_comments > 0 ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                          {stat.reports_without_comments}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        <span className={stat.reports_needing_chaser1 > 0 ? 'text-yellow-600 font-medium' : 'text-gray-500'}>
                          {stat.reports_needing_chaser1}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        <span className={stat.reports_needing_chaser2 > 0 ? 'text-red-600 font-medium' : 'text-gray-500'}>
                          {stat.reports_needing_chaser2}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        <span className={`${
                          stat.oldest_unresponded_days > 14 ? 'text-red-600 font-medium' :
                          stat.oldest_unresponded_days > 7 ? 'text-yellow-600 font-medium' :
                          'text-gray-500'
                        }`}>
                          {stat.oldest_unresponded_days > 0 ? `${stat.oldest_unresponded_days}d` : '-'}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          {stat.reports_needing_chaser1 > 0 && (
                            <button
                              onClick={() => handleSendChaserForAuthority(1, stat.local_authority)}
                              disabled={isDisabled}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-500 text-white rounded text-xs font-medium hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={`Send Chaser #1: ${stat.reports_needing_chaser1} reports`}
                            >
                              {isSendingC1 ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Send className="w-3 h-3" />
                              )}
                              C1
                            </button>
                          )}
                          {stat.reports_needing_chaser2 > 0 && (
                            <button
                              onClick={() => handleSendChaserForAuthority(2, stat.local_authority)}
                              disabled={isDisabled}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-500 text-white rounded text-xs font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={`Send Chaser #2: ${stat.reports_needing_chaser2} reports`}
                            >
                              {isSendingC2 ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Send className="w-3 h-3" />
                              )}
                              C2
                            </button>
                          )}
                          {stat.reports_needing_chaser1 === 0 && stat.reports_needing_chaser2 === 0 && (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
